import React, { Suspense } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { BrowserRouter } from 'react-router-dom';

// Component imports
import DesktopApp from './desktop/components/DesktopApp';
import MobileApp from './mobile/components/MobileApp';
import { AdminGuard } from './components/AdminGuard';

// Context imports
import { FirebaseAuthProvider } from './contexts/FirebaseAuthProvider';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { DarkModeProvider } from './contexts/DarkModeContext';
import ErrorBoundary from './components/ErrorBoundary';

// Lazy-loaded page components for code splitting
const PricingPage = React.lazy(() => import('./desktop/components/PricingPage'));
const FeaturesPage = React.lazy(() => import('./desktop/components/FeaturesPage'));
const AboutUs = React.lazy(() => import('./desktop/components/AboutUs'));
const InterviewSimulator = React.lazy(() => import('./desktop/components/InterviewSimulator'));
const BillingManagement = React.lazy(() => import('./desktop/components/BillingManagement'));
const SubscriptionSuccess = React.lazy(() => import('./desktop/components/SubscriptionSuccess'));
const LoginWrapper = React.lazy(() => import('./desktop/Auth/LoginWrapper'));
const AdminControlBoard = React.lazy(() => import('./desktop/components/AdminControlBoard'));
const ContactPage = React.lazy(() => import('./desktop/components/ContactPage'));
const AdminBlogDashboard = React.lazy(() => import('./desktop/components/AdminBlogDashboard'));
const BlogList = React.lazy(() => import('./pages/BlogList'));
const BlogPostView = React.lazy(() => import('./pages/BlogPostView'));
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage'));
const TermsPage = React.lazy(() => import('./pages/TermsPage'));
const FAQPage = React.lazy(() => import('./pages/FAQPage'));

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const App = () => {
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <FirebaseAuthProvider>
          <DarkModeProvider>
            <SubscriptionProvider>
              <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--background-color)' }}><div style={{ width: '40px', height: '40px', border: '3px solid rgba(52,152,219,0.25)', borderTopColor: '#3498DB', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /></div>}>
                <Routes>
                  <Route path="/" element={
                    <div className={isMobile ? 'mobile-app' : 'desktop-app'}>
                      {isMobile ? <MobileApp /> : <DesktopApp />}
                    </div>
                  } />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/features" element={<FeaturesPage />} />
                  <Route path="/about" element={<AboutUs />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/interview-simulator" element={<InterviewSimulator />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/faq" element={<FAQPage />} />
                  <Route 
                    path="/settings/billing" 
                    element={
                      <Elements stripe={stripePromise}>
                        <BillingManagement />
                      </Elements>
                    } 
                  />
                  <Route path="/subscription/success" element={<SubscriptionSuccess />} />
                  <Route path="/admin" element={<AdminControlBoard />} />
                  <Route 
                    path="/admin/blog" 
                    element={
                      <AdminGuard>
                        <AdminBlogDashboard />
                      </AdminGuard>
                    } 
                  />
                  <Route path="/blog" element={<BlogList />} />
                  <Route path="/blog/:slug" element={<BlogPostView />} />
                  <Route path="/subscription/canceled" element={<Navigate to="/pricing" />} />
                  <Route path="/sign-in" element={<LoginWrapper />} />
                  <Route path="/sign-up" element={<LoginWrapper />} />
                </Routes>
              </Suspense>
            </SubscriptionProvider>
          </DarkModeProvider>
        </FirebaseAuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
